import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { getApiErrorMessage } from "src/types/api";

interface MdtrCallbackParams {
  group_name: string;
  library_name: string;
  workflow_name: string;
  round: number;
  workflow_type?: string;
  file_type?: string;
}

// MDTR 전용 파라미터
export interface iMdtrUserParameterState {
  workflow_type: "mdtr" | "animal";
  expression_file_name: string | null;
  expression_file_ext: string | null;
  expression_file_info: {
    file_name: string;
    file_extension: string;
    file_size: number;
    s3_path: string;
  };
  input_type: "tpm" | "fpkm" | "count";
  mdtr_control_samples: string[];
}

const initialState: iMdtrUserParameterState = {
  workflow_type: "mdtr",
  expression_file_name: null,
  expression_file_ext: null,
  expression_file_info: {
    file_name: "",
    file_extension: "",
    file_size: 0,
    s3_path: "",
  },
  input_type: "tpm",
  mdtr_control_samples: [],
};

const splitExpressionFileName = (fileName?: string | null, fallbackExt?: string | null) => {
  const safeName = String(fileName || "").trim();
  const safeExt = String(fallbackExt || "").trim().replace(/^\./, "");
  const dotIndex = safeName.lastIndexOf(".");

  if (dotIndex > 0) {
    return {
      name: safeName.slice(0, dotIndex),
      ext: safeName.slice(dotIndex + 1) || safeExt,
    };
  }

  return {
    name: safeName,
    ext: safeExt,
  };
};

const getMdtrFileInfo = (response: any) =>
  response?.expression_file_info || response?.file_info || response || {};

const applyMdtrExpressionFileInfo = (state: iMdtrUserParameterState, response: any) => {
  const fileInfo = getMdtrFileInfo(response);
  const expressionFileName =
    response?.expression_file_name ||
    fileInfo.expression_file_name ||
    fileInfo.file_name;
  const expressionFileExt =
    response?.expression_file_ext ||
    fileInfo.expression_file_ext ||
    fileInfo.file_ext ||
    fileInfo.file_extension;

  if (expressionFileName) {
    const fileName = splitExpressionFileName(expressionFileName, expressionFileExt);
    state.expression_file_name = fileName.name;
    if (fileName.ext) state.expression_file_ext = fileName.ext;
  } else if (expressionFileExt) {
    state.expression_file_ext = String(expressionFileExt).replace(/^\./, "");
  }

  state.expression_file_info = {
    file_name: String(fileInfo.file_name || fileInfo.expression_file_name || expressionFileName || state.expression_file_info.file_name || ""),
    file_extension: String(fileInfo.file_extension || fileInfo.file_ext || fileInfo.expression_file_ext || expressionFileExt || state.expression_file_info.file_extension || "").replace(/^\./, ""),
    file_size: Number(fileInfo.file_size || state.expression_file_info.file_size || 0),
    s3_path: String(fileInfo.s3_path || state.expression_file_info.s3_path || ""),
  };
};

export const workFlowFilterCallbackAPI = createAsyncThunk(
  "userMdtrParameter/workFlowFilterCallback",
  async (params: MdtrCallbackParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/filter-callback/`,
        params,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

export const WorkFlowFileCallBackAPI = createAsyncThunk(
  "userMdtrParameter/WorkFlowFileCallBack",
  async (params: MdtrCallbackParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const response = await axiosInstance.post(
        `${djangoEndpoint}/workflow/file-callback/`,
        params,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  }
);

// 독립 파라미터 상태
const mdtrUserParameterSlice = createSlice({
  name: "userMdtrParameter",
  initialState,
  reducers: {
    setMdtrWorkflowType: (state, action: PayloadAction<"mdtr" | "animal">) => {
      state.workflow_type = action.payload;
    },
    setMdtrExpressionFileName: (state, action: PayloadAction<string | null>) => {
      state.expression_file_name = action.payload;
      state.expression_file_info.file_name = action.payload || "";
    },
    setMdtrExpressionFileExt: (state, action: PayloadAction<string | null>) => {
      state.expression_file_ext = action.payload;
      state.expression_file_info.file_extension = action.payload || "";
    },
    setMdtrInputType: (state, action: PayloadAction<"tpm" | "fpkm" | "count">) => {
      state.input_type = action.payload;
    },
    setMdtrControlSamples: (state, action: PayloadAction<string[]>) => {
      state.mdtr_control_samples = action.payload;
    },
    resetMdtrParameters: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(workFlowFilterCallbackAPI.fulfilled, (state, action) => {
        const response = action.payload?.data || action.payload || {};
        const inputType = response.input_type || response.mdtr_input_type;
        const control = response.control || response.mdtr_control_samples || response.control_samples;

        applyMdtrExpressionFileInfo(state, response);

        if (inputType === "tpm" || inputType === "fpkm" || inputType === "count") {
          state.input_type = inputType;
        }
        if (typeof control === "string") {
          state.mdtr_control_samples = control.split("/").filter(Boolean);
        } else if (Array.isArray(control)) {
          state.mdtr_control_samples = control;
        }
      })
      .addCase(WorkFlowFileCallBackAPI.fulfilled, (state, action) => {
        const response = action.payload?.data || action.payload || {};
        applyMdtrExpressionFileInfo(state, response);
      });
  },
});

export const {
  setMdtrWorkflowType,
  setMdtrExpressionFileName,
  setMdtrExpressionFileExt,
  setMdtrInputType,
  setMdtrControlSamples,
  resetMdtrParameters,
} = mdtrUserParameterSlice.actions;

export default mdtrUserParameterSlice.reducer;
